/**
 * web/src/components/gates/FormRenderer.tsx
 * Renders fields from a gateData JSON Schema (CONTEXT.md D-4.1).
 * Supports types: string (default → text input; format='textarea' → textarea;
 * format='date' → date input; format='email' → email input; enum → select),
 * integer/number → number input, boolean → checkbox. Fields named in
 * `readonly: [...]` (or via the `readonlyFields` prop) are rendered readonly
 * with the muted bg from the handoff.
 *
 * RHF integration: parent owns the FormProvider; this component renders
 * fields via useFormContext. Validation is on submit only (D-4.2);
 * field-level errors come from server VALIDATION_ERROR response and are
 * mapped via parent's onError handler (PATTERNS lines 1938-1948).
 *
 * Server-side authority: the SERVER's onApproval handler re-validates the
 * gate schema (Pydantic) — defense in depth, D-4.1 + D-4.2. No client-side
 * schema validation library is used here; validation is RHF rules on submit.
 * Client-side rendering of unknown schema types falls back to a plain
 * text input — no eval, no dynamic require (T-05-09-01 mitigation).
 */
import { useFormContext } from 'react-hook-form';
import { Input } from '../shared/Input';
import { Textarea } from '../shared/Textarea';
import { Select } from '../shared/Select';
import { cn } from '../../lib/cn';
import { titleCase } from '../../lib/format';

/**
 * JSON Schema subset we render. Intentionally narrow (string/integer/number/
 * boolean + format=textarea/date/email + enum) so a malicious schema can't
 * inject a non-text input type. Anything outside this subset falls through
 * to a plain text input.
 */
export interface JSONSchema {
  type?: string;
  properties?: Record<string, JSONSchemaProperty>;
  required?: string[];
  /** Field names that should be rendered read-only. */
  readonly?: string[];
}

/**
 * Single property descriptor in a JSONSchema.properties map. `title`
 * overrides the auto-humanized field label; `description` renders as
 * muted helper text below the input.
 */
export interface JSONSchemaProperty {
  type?: 'string' | 'integer' | 'number' | 'boolean';
  format?: 'textarea' | 'date' | 'email';
  enum?: readonly string[];
  title?: string;
  description?: string;
}

/** Public props for FormRenderer. */
export interface FormRendererProps {
  schema: JSONSchema;
  /** Additional readonly field names — merged with `schema.readonly`. */
  readonlyFields?: readonly string[];
  className?: string;
}

/**
 * Renders all properties of a JSON Schema as RHF-registered fields.
 * Pure presentation — caller owns the <FormProvider /> + submission.
 *
 * @param schema JSON Schema describing the editable shape
 * @param readonlyFields extra readonly field names (merged with schema.readonly)
 * @param className optional Tailwind classes for the grid wrapper
 * @sideEffects calls form.register for each property
 * @idempotent yes
 */
export function FormRenderer({ schema, readonlyFields = [], className }: FormRendererProps) {
  const form = useFormContext();
  const props = schema.properties ?? {};
  const required = new Set(schema.required ?? []);
  const readonly = new Set([...(schema.readonly ?? []), ...readonlyFields]);

  return (
    <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-4', className)}>
      {Object.entries(props).map(([name, p]) => {
        const isRequired = required.has(name);
        const isReadonly = readonly.has(name);
        const errorMsg = (form.formState.errors[name]?.message as string | undefined) ?? undefined;
        const fullWidth = p.format === 'textarea';
        const id = `field-${name}`;
        return (
          <div key={name} className={cn('flex flex-col gap-1.5', fullWidth && 'sm:col-span-2')}>
            <label htmlFor={id} className="text-[12px] font-medium text-text-muted">
              {p.title ?? titleCase(name)}
              {isRequired && (
                <span className="text-failed ml-0.5" aria-hidden="true">
                  *
                </span>
              )}
            </label>
            {p.format === 'textarea' ? (
              <Textarea
                id={id}
                rows={4}
                readOnly={isReadonly}
                aria-invalid={errorMsg ? true : undefined}
                {...form.register(name)}
              />
            ) : p.enum && p.enum.length > 0 ? (
              <Select
                id={id}
                disabled={isReadonly}
                aria-invalid={errorMsg ? true : undefined}
                {...form.register(name)}
              >
                {!isRequired && <option value="">—</option>}
                {p.enum.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            ) : p.type === 'boolean' ? (
              <input
                id={id}
                type="checkbox"
                disabled={isReadonly}
                className="w-4 h-4 accent-accent"
                {...form.register(name)}
              />
            ) : (
              <Input
                id={id}
                type={
                  p.type === 'integer' || p.type === 'number'
                    ? 'number'
                    : p.format === 'date'
                      ? 'date'
                      : p.format === 'email'
                        ? 'email'
                        : 'text'
                }
                readOnly={isReadonly}
                aria-invalid={errorMsg ? true : undefined}
                {...form.register(
                  name,
                  p.type === 'integer' || p.type === 'number' ? { valueAsNumber: true } : undefined,
                )}
              />
            )}
            {p.description && <span className="text-[11px] text-text-subtle">{p.description}</span>}
            {errorMsg && (
              <span className="text-[11px] text-failed" role="alert">
                {errorMsg}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
